/**
 * Utility function for tail truncating filenames while preserving file extensions
 */

/**
 * Truncates a filename from the end while preserving the file extension
 *
 * @param filename - The filename to truncate
 * @param maxLength - Maximum length of the truncated filename (default: 20)
 * @returns The truncated filename with extension preserved
 *
 * @example
 * truncateFilename("very-long-document-name.pdf", 20)
 * // Returns: "very-long-do...pdf"
 *
 * truncateFilename("screenshot.png", 20)
 * // Returns: "screenshot.png" (no truncation needed)
 */
export const truncateFilename = (filename: string, maxLength = 20): string => {
  if (filename.length <= maxLength) {
    return filename;
  }

  // Find the last dot to separate extension
  const lastDotIndex = filename.lastIndexOf('.');

  // If no extension or the dot is at the beginning, just truncate normally
  if (lastDotIndex <= 0 || lastDotIndex === filename.length - 1) {
    return filename.substring(0, maxLength - 3) + '...';
  }

  const extension = filename.substring(lastDotIndex);
  const nameWithoutExtension = filename.substring(0, lastDotIndex);

  // If extension is too long (more than half the max length), just truncate normally
  if (extension.length > maxLength / 2) {
    return filename.substring(0, maxLength - 3) + '...';
  }

  // Calculate how much space we have for the name part
  const availableForName = maxLength - 3;

  if (availableForName <= 0) {
    return filename.substring(0, maxLength - 3) + '...';
  }

  return nameWithoutExtension.substring(0, availableForName) + '...';
};

/**
 * Truncates text with smart word boundary detection
 *
 * @param text - The text to truncate
 * @param maxLength - Maximum length of the truncated text (default: 60)
 * @returns The truncated text
 */
export const truncateText = (text: string, maxLength = 60): string => {
  if (text.length <= maxLength) {
    return text;
  }

  // Try to break at word boundary
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > maxLength * 0.7) {
    // If we found a space in the last 30% of the text
    return truncated.substring(0, lastSpace) + '...';
  }

  return truncated + '...';
};
